import { z } from 'zod';

import { PaginationSchema } from '../../pagination.js';

export const PlaceFiscalOperationIdParamsSchema = z.looseObject({
  placeId: z.uuid(),
  operationId: z.uuid(),
});

export const TAX_REGIMES = ['SIMPLES_NACIONAL', 'LUCRO_PRESUMIDO', 'LUCRO_REAL', 'MEI'] as const;
export const CRT_CODES = ['1', '2', '3'] as const;
export const CERTIFICATE_TYPES = ['A1', 'A3'] as const;

export const UpsertPlaceTaxProfileBodySchema = z.looseObject({
  taxRegime: z.enum(TAX_REGIMES),
  crt: z.enum(CRT_CODES),
  mainCnaeCode: z.string().max(10).nullable().optional(),
  simplesAnnex: z.string().max(10).nullable().optional(),
  simplesEffectiveRateBp: z.number().int().min(0).nullable().optional(),
  validFrom: z.iso.date().optional(),
  validUntil: z.iso.date().nullable().optional(),
});

export const UpsertPlaceTaxProfileResponseSchema = z.looseObject({
  profileId: z.uuid(),
  placeId: z.uuid(),
  validFrom: z.iso.date(),
  validUntil: z.iso.date().nullable(),
});

export const ResolvePlaceTaxProfileQuerySchema = z.looseObject({
  competenceDate: z.iso.date(),
});

export const PlaceTaxProfileActiveResponseSchema = z.looseObject({
  profileId: z.uuid(),
  placeId: z.uuid(),
  taxRegime: z.string(),
  crt: z.string(),
  mainCnaeCode: z.string().nullable(),
  simplesAnnex: z.string().nullable(),
  simplesEffectiveRateBp: z.number().nullable(),
  validFrom: z.iso.date(),
  validUntil: z.iso.date().nullable(),
});

export const PlaceTaxProfileListItemSchema = z.looseObject({
  profileId: z.uuid(),
  placeId: z.uuid(),
  taxRegime: z.string(),
  crt: z.string(),
  mainCnaeCode: z.string().nullable(),
  simplesAnnex: z.string().nullable(),
  simplesEffectiveRateBp: z.number().nullable(),
  validFrom: z.iso.date(),
  validUntil: z.iso.date().nullable(),
});

export const ListPlaceTaxProfilesResponseSchema = z.looseObject({
  items: z.array(PlaceTaxProfileListItemSchema),
});

export const PlaceFiscalContextResponseSchema = z.looseObject({
  placeId: z.uuid(),
  name: z.string(),
  documentNumber: z.string(),
  stateRegistration: z.string().nullable(),
});

export const RegisterPlaceDigitalCertificateBodySchema = z.looseObject({
  certificateType: z.enum(CERTIFICATE_TYPES),
  serialNumber: z.string().min(1),
  subjectCnpj: z.string().length(14),
  storageReference: z.string().min(1),
  expiresAt: z.iso.datetime(),
  validFrom: z.iso.date(),
  validUntil: z.iso.date().nullable().optional(),
});

export const RegisterPlaceDigitalCertificateResponseSchema = z.looseObject({
  certificateId: z.uuid(),
  placeId: z.uuid(),
  validFrom: z.iso.date(),
  expiresAt: z.iso.datetime(),
});

export const GetActivePlaceDigitalCertificateQuerySchema = z.looseObject({
  at: z.iso.datetime().optional(),
});

export const PlaceDigitalCertificateActiveResponseSchema = z.looseObject({
  certificateId: z.uuid(),
  placeId: z.uuid(),
  certificateType: z.string(),
  serialNumber: z.string(),
  subjectCnpj: z.string(),
  storageReference: z.string(),
  expiresAt: z.iso.datetime(),
  validFrom: z.iso.date(),
  validUntil: z.iso.date().nullable(),
});

export const RegisterPlaceFiscalOperationBodySchema = z.looseObject({
  operationDefinitionCode: z.string().min(1).max(50),
  code: z.string().min(1).max(50).optional(),
  name: z.string().max(255).optional(),
  defaultCfopCode: z.string().length(4).optional(),
});

export const UpdatePlaceFiscalOperationBodySchema = z.looseObject({
  name: z.string().min(1).optional(),
  defaultCfopCode: z.string().length(4).nullable().optional(),
});

export const PlaceFiscalOperationResponseSchema = z.looseObject({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  operationDefinitionCode: z.string(),
  direction: z.string(),
  operationKind: z.string(),
  documentKind: z.string(),
  updatedAt: z.iso.datetime(),
});

export const ListPlaceFiscalOperationsQuerySchema = PaginationSchema;

export const ListPlaceFiscalOperationsResponseSchema = z.looseObject({
  items: z.array(
    z.looseObject({
      id: z.uuid(),
      code: z.string(),
      name: z.string(),
      operationDefinitionCode: z.string(),
      operationNature: z.string().nullable(),
      direction: z.string().nullable(),
      operationKind: z.string().nullable(),
      documentKind: z.string().nullable(),
      defaultCfopCode: z.string().nullable(),
    }),
  ),
  pagination: z.looseObject({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
  }),
});

export const SoftDeletePlaceFiscalOperationBodySchema = z.looseObject({
  deleteReason: z.string().max(500).nullable().optional(),
});

export type UpsertPlaceTaxProfileBody = z.infer<typeof UpsertPlaceTaxProfileBodySchema>;
export type RegisterPlaceDigitalCertificateBody = z.infer<
  typeof RegisterPlaceDigitalCertificateBodySchema
>;
export type RegisterPlaceFiscalOperationBody = z.infer<
  typeof RegisterPlaceFiscalOperationBodySchema
>;
export type UpdatePlaceFiscalOperationBody = z.infer<typeof UpdatePlaceFiscalOperationBodySchema>;
export type ListPlaceFiscalOperationsQuery = z.infer<typeof ListPlaceFiscalOperationsQuerySchema>;
